// FILE: src/pages/admin/AdminResourceSlidesPage.jsx
// A lesson's slides and videos (WP-5; R1, M8, matrix 0.9, 4.4, 9.7): each
// slide's name, nominal time in seconds (the minimum-dwell rule, WP-3),
// page, activity (CD3.3c-d) and CC code (Unit → Lesson → ILO, MTO Part B),
// and each video's title, length and slide (MTO 3.5). Totals and the
// lesson's learning outcomes are recomputed by the server on save.
//
// Problems use the server's field paths (slides[0].slideTime, media[1].
// durationSec): found here first or sent back by the server, they are listed
// at the top, each linked to its field, and shown under the field.
//
//   GET   /api/admin/resources/:id
//   PATCH /api/admin/resources/:id   { slides, media }
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Link, MenuItem, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../../components/ui";
import { ProblemSummary } from "../../components/admin/ContentBits";
import {
  CHALLENGE_TYPES,
  VIDEO_MAX_SEC,
  curriculumName,
  formatSeconds,
  mediaRows,
  newMediaRow,
  newSlideRow,
  parseFieldPath,
  problemList,
  slideRows,
  slideTotals,
  slidesPayload,
  validateSlides
} from "../../components/admin/content";
import { errorPayload } from "../../components/admin/apiError";
import { errorCode, errorMessage, messageFor } from "../../i18n/errors";
import { challengeTypeLabel } from "../../i18n/labels";

const EMPTY = {};

/** Server field path → the input's id ("slides[2].ccCode" → "slides-2-ccCode"). */
export function inputIdFor(field) {
  const { list, index, key } = parseFieldPath(field);
  if (list === "slides") return `slides-${index}-${key === "slides" ? "slideName" : key}`;
  if (list === "media") return `media-${index}-${key === "media" ? "title" : key}`;
  if (field === "slides") return "add-slide";
  if (field === "media") return "add-video";
  return null;
}

/** { slides: { 0: { slideTime: "…" } }, media: { … } } from the problem list. */
function errorTree(items) {
  const tree = { slides: {}, media: {} };
  for (const it of items) {
    const { list, index, key } = parseFieldPath(it.field);
    if (list !== "slides" && list !== "media") continue;
    const k = key === list ? (list === "slides" ? "slideName" : "title") : key;
    tree[list][index] = { ...(tree[list][index] || {}), [k]: tree[list][index]?.[k] || it.message };
  }
  return tree;
}

const fieldsetSx = {
  m: 0,
  p: { xs: 2, sm: 2.5 },
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  minWidth: 0
};

const SlideRow = memo(function SlideRow({ row, index, errors, disabled, onChange, onRemove }) {
  const id = (k) => `slides-${index}-${k}`;
  const change = (e) => onChange(index, e.target.name, e.target.value);
  return (
    <Box component="fieldset" sx={fieldsetSx} data-testid={`slide-${index + 1}`}>
      <Typography component="legend" sx={{ fontWeight: 650, px: 0.5 }}>
        Slide {index + 1}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          mt: 1,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "minmax(0, 2fr) repeat(2, minmax(0, 0.8fr)) minmax(0, 1.4fr) minmax(0, 1fr)" }
        }}
      >
        <TextField
          id={id("slideName")}
          name="slideName"
          label="Name"
          value={row.slideName}
          onChange={change}
          error={!!errors.slideName}
          helperText={errors.slideName}
          required
          disabled={disabled}
          sx={{ gridColumn: { sm: "1 / -1", lg: "auto" } }}
        />
        <TextField
          id={id("slideTime")}
          name="slideTime"
          label="Time (seconds)"
          value={row.slideTime}
          onChange={change}
          error={!!errors.slideTime}
          helperText={errors.slideTime || (row.slideTime !== "" && Number.isFinite(Number(row.slideTime)) ? formatSeconds(row.slideTime) : undefined)}
          slotProps={{ htmlInput: { inputMode: "decimal" } }}
          required
          disabled={disabled}
        />
        <TextField
          id={id("pageIndex")}
          name="pageIndex"
          label="Page"
          value={row.pageIndex}
          onChange={change}
          error={!!errors.pageIndex}
          helperText={errors.pageIndex}
          slotProps={{ htmlInput: { inputMode: "numeric" } }}
          disabled={disabled}
        />
        <TextField
          select
          id={id("challengeType")}
          name="challengeType"
          label="Activity"
          value={row.challengeType}
          onChange={change}
          error={!!errors.challengeType}
          helperText={errors.challengeType}
          disabled={disabled}
        >
          {CHALLENGE_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {challengeTypeLabel(t)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          id={id("ccCode")}
          name="ccCode"
          label="CC code"
          value={row.ccCode}
          onChange={change}
          error={!!errors.ccCode}
          helperText={errors.ccCode}
          slotProps={{ htmlInput: { spellCheck: false, autoCapitalize: "characters" } }}
          disabled={disabled}
        />
      </Box>
      {!disabled && (
        <Button
          onClick={() => onRemove(index)}
          startIcon={<DeleteOutlineIcon />}
          color="error"
          aria-label={`Remove slide ${index + 1}`}
          sx={{ mt: 1.5 }}
        >
          Remove
        </Button>
      )}
    </Box>
  );
});

const VideoRow = memo(function VideoRow({ row, index, errors, disabled, onChange, onRemove }) {
  const id = (k) => `media-${index}-${k}`;
  const change = (e) => onChange(index, e.target.name, e.target.value);
  const seconds = Number(row.durationSec);
  const tooLong = Number.isFinite(seconds) && seconds > VIDEO_MAX_SEC;
  return (
    <Box component="fieldset" sx={fieldsetSx} data-testid={`video-${index + 1}`}>
      <Typography component="legend" sx={{ fontWeight: 650, px: 0.5 }}>
        Video {index + 1}
      </Typography>
      <Box sx={{ display: "grid", gap: 2, mt: 1, gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)" } }}>
        <TextField id={id("title")} name="title" label="Title" value={row.title} onChange={change} error={!!errors.title} helperText={errors.title} disabled={disabled} />
        <TextField
          id={id("durationSec")}
          name="durationSec"
          label="Length (seconds)"
          value={row.durationSec}
          onChange={change}
          error={!!errors.durationSec}
          helperText={
            errors.durationSec ||
            (row.durationSec !== "" && Number.isFinite(seconds)
              ? `${formatSeconds(seconds)}${tooLong ? ": longer than the 7-minute limit" : ""}`
              : undefined)
          }
          slotProps={{ htmlInput: { inputMode: "decimal" } }}
          required
          disabled={disabled}
        />
        <TextField
          id={id("slideIndex")}
          name="slideIndex"
          label="On slide"
          value={row.slideIndex}
          onChange={change}
          error={!!errors.slideIndex}
          helperText={errors.slideIndex}
          slotProps={{ htmlInput: { inputMode: "numeric" } }}
          disabled={disabled}
        />
      </Box>
      {!disabled && (
        <Button onClick={() => onRemove(index)} startIcon={<DeleteOutlineIcon />} color="error" aria-label={`Remove video ${index + 1}`} sx={{ mt: 1.5 }}>
          Remove
        </Button>
      )}
    </Box>
  );
});

export default function AdminResourceSlidesPage() {
  const { id } = useParams();
  const [state, setState] = useState({ resource: null, loading: true, error: null });
  const [slides, setSlides] = useState([]);
  const [media, setMedia] = useState([]);
  const [problems, setProblems] = useState([]);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const summaryRef = useRef(null);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/admin/resources/${encodeURIComponent(id)}`)
      .then((resource) => {
        setState({ resource, loading: false, error: null });
        setSlides(slideRows(resource.slides));
        setMedia(mediaRows(resource.media));
      })
      .catch((error) => setState({ resource: null, loading: false, error: error || "INTERNAL_ERROR" }));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const touched = useCallback((list, index, key) => {
    setSaved(false);
    setProblems((ps) => ps.filter((p) => p.field !== `${list}[${index}].${key}`));
  }, []);

  const changeSlide = useCallback(
    (index, key, value) => {
      setSlides((rows) => rows.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
      touched("slides", index, key);
    },
    [touched]
  );
  const changeVideo = useCallback(
    (index, key, value) => {
      setMedia((rows) => rows.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
      touched("media", index, key);
    },
    [touched]
  );
  // Removing a row shifts the field paths, so earlier problems no longer apply.
  const removeSlide = useCallback((index) => {
    setSlides((rows) => rows.filter((_, i) => i !== index));
    setProblems([]);
    setSaved(false);
  }, []);
  const removeVideo = useCallback((index) => {
    setMedia((rows) => rows.filter((_, i) => i !== index));
    setProblems([]);
    setSaved(false);
  }, []);

  const items = useMemo(() => problemList(problems), [problems]);
  const tree = useMemo(() => errorTree(items), [items]);
  const totals = slideTotals(slides);

  const { resource, loading, error } = state;
  if (!resource) {
    if (loading) return <LoadingState variant="page" label="Loading the slides…" />;
    const missing = ["RESOURCE_NOT_FOUND", "NOT_FOUND"].includes(errorCode(error));
    return (
      <Box>
        <PageHeader title="Slides and videos" breadcrumbs={[{ label: "Lessons", to: "/admin/resources" }, { label: "Slides and videos" }]} />
        {missing ? (
          <EmptyState
            title={messageFor("RESOURCE_NOT_FOUND")}
            description="It may have been deleted. Go back to the lesson list and choose another."
            action={
              <Button component={RouterLink} to="/admin/resources" variant="contained">
                Back to lessons
              </Button>
            }
          />
        ) : (
          <ErrorState error={error} title="We couldn't load this lesson" onRetry={load} />
        )}
      </Box>
    );
  }

  const title = resource.title || "Untitled lesson";
  const frozen = !!resource.curriculum && resource.curriculum.status !== "DRAFT";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    const found = validateSlides(slides, media);
    setProblems(found);
    if (found.length) {
      setTimeout(() => summaryRef.current?.focus(), 0);
      return;
    }
    setSubmitting(true);
    try {
      const updated = await fetchWithAuth(`/api/admin/resources/${encodeURIComponent(resource._id)}`, {
        method: "PATCH",
        body: JSON.stringify(slidesPayload(slides, media))
      });
      setState((s) => ({ ...s, resource: { ...s.resource, ...updated } }));
      setSlides(slideRows(updated.slides));
      setMedia(mediaRows(updated.media));
      setSaved(true);
    } catch (err) {
      const body = errorPayload(err);
      setProblems(errorCode(err) === "VALIDATION_FAILED" && Array.isArray(body.problems) ? body.problems : []);
      setSaveError(err || "INTERNAL_ERROR");
      setTimeout(() => summaryRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title={`Slides and videos: ${title}`}
        breadcrumbs={[
          { label: "Lessons", to: "/admin/resources" },
          { label: title, to: `/admin/resources/${resource._id}` },
          { label: "Slides and videos" }
        ]}
        description="Enter each slide as it appears in the deck's publish list: its name, its time in seconds, its page, the activity on it and its CC code. The lesson's totals and learning outcomes are worked out when you save."
      />

      {frozen && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          <AlertTitle>These slides can't be changed</AlertTitle>
          The lesson belongs to {curriculumName(resource.curriculum)}, which is {resource.curriculum.status === "RETIRED" ? "retired" : "approved"}. Make a
          new version on the{" "}
          <Link component={RouterLink} to={`/admin/curricula/${resource.curriculum.id}`}>
            curriculum's page
          </Link>{" "}
          to change them.
        </Alert>
      )}

      <Box component="form" noValidate onSubmit={handleSubmit} aria-label="Slides and videos">
        <Stack spacing={2.5}>
          {(items.length > 0 || saveError) && (
            <ProblemSummary
              ref={summaryRef}
              title={saveError ? "We couldn't save the slides" : "Check the slides and videos"}
              intro={saveError ? `${errorMessage(saveError)} Your changes are still on this page.` : undefined}
              items={items}
              idFor={inputIdFor}
            />
          )}
          {saved && (
            <Alert severity="success" role="status">
              Slides and videos saved.
            </Alert>
          )}

          <Section id="slides" title="Slides" description={totals.text}>
            <Stack spacing={2}>
              {slides.length === 0 && <Typography sx={{ color: "text.secondary" }}>No slides yet. Add the first one below.</Typography>}
              {slides.map((row, i) => (
                <SlideRow
                  key={row.key}
                  row={row}
                  index={i}
                  errors={tree.slides[i] || EMPTY}
                  disabled={frozen}
                  onChange={changeSlide}
                  onRemove={removeSlide}
                />
              ))}
            </Stack>
            {!frozen && (
              <Button
                id="add-slide"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setSlides((rows) => [...rows, newSlideRow(rows.length + 1)])}
                sx={{ mt: 2 }}
              >
                Add a slide
              </Button>
            )}
          </Section>

          <Section
            id="videos"
            title="Videos"
            description="Each video must be 7 minutes or shorter, and an hour of lessons can have at most 20 minutes of video (MTO 3.5). Enter every video so the curriculum check can measure them."
          >
            <Stack spacing={2}>
              {media.length === 0 && <Typography sx={{ color: "text.secondary" }}>No videos entered.</Typography>}
              {media.map((row, i) => (
                <VideoRow
                  key={row.key}
                  row={row}
                  index={i}
                  errors={tree.media[i] || EMPTY}
                  disabled={frozen}
                  onChange={changeVideo}
                  onRemove={removeVideo}
                />
              ))}
            </Stack>
            {!frozen && (
              <Button id="add-video" variant="outlined" startIcon={<AddIcon />} onClick={() => setMedia((rows) => [...rows, newMediaRow()])} sx={{ mt: 2 }}>
                Add a video
              </Button>
            )}
          </Section>

          <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ justifyContent: "flex-end" }}>
            <Button component={RouterLink} to={`/admin/resources/${resource._id}`} variant="outlined">
              Back to the lesson
            </Button>
            <Button type="submit" variant="contained" startIcon={<SaveOutlinedIcon />} disabled={submitting || frozen}>
              {submitting ? "Saving…" : "Save slides and videos"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
